import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import {
  Sparkles,
  RefreshCw,
  Loader2,
  CheckCircle2,
  ShieldAlert,
  Lightbulb,
} from "lucide-react";

/**
 * AI destekli finansal analiz kartı.
 * tip: "dashboard" | "butce"
 */
export const AiYorum = ({ tip, yil = 2026, donem = "yil", baslik = "AI Finansal Analiz" }) => {
  const [yorum, setYorum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchCached = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/ai-yorum", { params: { tip, yil, donem } });
      setYorum(res.data?.id ? res.data : null);
    } catch {
      setYorum(null);
    } finally {
      setLoading(false);
    }
  }, [tip, yil, donem]);

  useEffect(() => {
    fetchCached();
  }, [fetchCached]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/ai-yorum", { tip, yil, donem });
      setYorum(res.data);
      toast.success("AI analizi oluşturuldu.");
    } catch (err) {
      toast.error(getErrMsg(err, "AI analizi oluşturulamadı."));
    } finally {
      setGenerating(false);
    }
  };

  const ic = yorum?.icerik;

  const kutular = ic
    ? [
        { baslik: "GÜÇLÜ YÖNLER", icon: CheckCircle2, renk: "border-emerald-200 bg-emerald-50/60", ikonRenk: "text-emerald-600", maddeler: ic.guclu_yonler || [], testid: "ai-guclu" },
        { baslik: "RİSKLER", icon: ShieldAlert, renk: "border-red-200 bg-red-50/60", ikonRenk: "text-red-600", maddeler: ic.riskler || [], testid: "ai-riskler" },
        { baslik: "ÖNERİLER", icon: Lightbulb, renk: "border-blue-200 bg-blue-50/60", ikonRenk: "text-blue-600", maddeler: ic.oneriler || [], testid: "ai-oneriler" },
      ]
    : [];

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid={`ai-yorum-${tip}`}>
      <div className="flex flex-wrap items-center gap-2 border-b bg-[hsl(var(--primary))] px-4 py-3">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--accent))]/20">
          <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" />
        </span>
        <h3 className="font-display text-sm font-semibold text-white">{baslik}</h3>
        {yorum && (
          <Badge className="bg-white/10 text-white/80 border border-white/15 hover:bg-white/10 text-[10px]" data-testid="ai-yorum-tarih">
            {formatDateTime(yorum.created_at)}
          </Badge>
        )}
        <div className="ml-auto">
          <Button
            size="sm"
            onClick={generate}
            disabled={generating || loading}
            className="h-8 gap-1.5 bg-[hsl(var(--accent))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
            data-testid="ai-yorum-olustur-button"
          >
            {generating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Analiz ediliyor…
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                {yorum ? "Yenile" : "Analiz Oluştur"}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <div className="grid sm:grid-cols-3 gap-3">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          </div>
        ) : generating ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="ai-yorum-yukleniyor">
            <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--accent))]" />
            <p className="text-sm text-muted-foreground">
              Finansal verileriniz analiz ediliyor, bu işlem 10-30 saniye sürebilir…
            </p>
          </div>
        ) : !ic ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="ai-yorum-bos">
            <Sparkles className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground max-w-md">
              Henüz analiz oluşturulmadı. “Analiz Oluştur” butonuna basarak seçili dönemin
              verilerine dayalı AI destekli finansal yorum alın.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-foreground/90 rounded-lg border bg-muted/30 p-3" data-testid="ai-genel-degerlendirme">
              {ic.genel_degerlendirme}
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {kutular.map((k) => (
                <div key={k.baslik} className={`rounded-xl border p-3.5 ${k.renk}`} data-testid={k.testid}>
                  <div className={`flex items-center gap-1.5 ${k.ikonRenk}`}>
                    <k.icon className="h-4 w-4" />
                    <span className="text-[11px] font-bold tracking-wide">{k.baslik}</span>
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {k.maddeler.length === 0 ? (
                      <li className="text-xs text-muted-foreground">Not bulunmuyor.</li>
                    ) : (
                      k.maddeler.map((m, i) => (
                        <li key={i} className="text-xs leading-relaxed text-foreground/85">• {m}</li>
                      ))
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
